import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

interface Subscription {
  plan: string;
  product_limit: number;
}

export const useSubscriptionLimit = () => {
  const navigate = useNavigate();

  const { data: subscription, isLoading } = useQuery({
    queryKey: ["subscription"],
    queryFn: async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Non authentifié");

        // Vérifier d'abord s'il existe une limite personnalisée
        const { data: customLimit, error: customLimitError } = await supabase
          .from("custom_product_limits")
          .select("product_limit")
          .eq("owner_id", user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (customLimitError) {
          console.error("Erreur lors de la récupération de la limite personnalisée:", customLimitError);
        }

        if (customLimit) {
          console.log("Limite personnalisée trouvée:", customLimit.product_limit);
          return {
            plan: "custom",
            product_limit: customLimit.product_limit
          };
        }

        // Si pas de limite personnalisée, vérifier l'abonnement
        const { data: subscriptionData, error: subscriptionError } = await supabase
          .from("subscriptions")
          .select("*")
          .eq("owner_id", user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (subscriptionError) {
          console.error("Erreur lors de la récupération de l'abonnement:", subscriptionError);
          return {
            plan: "free",
            product_limit: 3
          };
        }

        if (!subscriptionData) {
          console.log("Aucun abonnement trouvé, utilisation du plan gratuit");
          return {
            plan: "free",
            product_limit: 3
          };
        }

        // Si c'est un plan standard, on force la limite à 20
        if (subscriptionData.plan === 'standard') {
          return {
            ...subscriptionData,
            product_limit: 20
          };
        }

        // Si c'est un plan premium, on met -1 pour indiquer illimité
        if (subscriptionData.plan === 'premium') {
          return {
            ...subscriptionData,
            product_limit: -1
          };
        }

        return subscriptionData;
      } catch (error) {
        console.error("Erreur lors de la récupération des limites:", error);
        return {
          plan: "free",
          product_limit: 3
        };
      }
    },
  });

  const checkProductLimit = async (currentCount: number) => {
    if (!subscription) {
      console.log("Aucun abonnement trouvé, utilisation de la limite du plan gratuit");
      return currentCount < 3;
    }

    console.log("Vérification de la limite:", {
      currentCount,
      limit: subscription.product_limit,
      plan: subscription.plan
    });

    // Si product_limit est -1 (premium), pas de limite
    if (subscription.product_limit === -1) {
      console.log("Pas de limite pour le plan premium");
      return true;
    }

    // Vérifier si la limite est atteinte
    if (currentCount >= subscription.product_limit) {
      toast.error(
        <div className="space-y-2">
          <p>
            Vous avez atteint la limite de {subscription.product_limit} produits de votre plan {subscription.plan}.
          </p>
          <p>
            Limites par plan :
            <br />- Gratuit : 3 produits
            <br />- Standard : 20 produits
            <br />- Premium : Illimité
            {subscription.plan === 'custom' && <><br />- Personnalisé : {subscription.product_limit} produits</>}
          </p>
          <p className="text-sm text-gray-500">
            Pour changer de plan, allez dans Paramètres &gt; Préférences &gt; Abonnement
          </p>
          <button
            onClick={() => navigate("/owner/settings")}
            className="text-blue-500 hover:text-blue-600 underline"
          >
            Accéder aux paramètres
          </button>
        </div>
      );
      return false;
    }

    return true;
  };

  const hasReachedLimit = (currentCount: number) => {
    if (!subscription) return currentCount >= 3;
    if (subscription.product_limit === -1) return false;
    return currentCount >= subscription.product_limit;
  };

  return {
    subscription,
    isLoading,
    checkProductLimit,
    hasReachedLimit,
    currentLimit: subscription?.product_limit || 3
  };
};